import { faCalendar } from '@fortawesome/pro-solid-svg-icons';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { cn } from '../../utils/cn';
import { Icon } from '../base/Icon';
import { Text } from '../base/Text';

export interface DateRangeOption {
	id: string;
	label: string;
	value: {
		startDate: Date | null;
		endDate: Date | null;
	};
}

const getQuarter = (date: Date): number => Math.floor(date.getMonth() / 3);

export const DATE_RANGE_OPTIONS: DateRangeOption[] = [
	{
		id: 'today',
		label: 'Today',
		value: {
			startDate: new Date(new Date().setHours(0, 0, 0, 0)),
			endDate: new Date(new Date().setHours(23, 59, 59, 999)),
		},
	},
	{
		id: 'yesterday',
		label: 'Yesterday',
		value: {
			startDate: new Date(
				new Date(Date.now() - 24 * 60 * 60 * 1000).setHours(0, 0, 0, 0),
			),
			endDate: new Date(
				new Date(Date.now() - 24 * 60 * 60 * 1000).setHours(23, 59, 59, 999),
			),
		},
	},
	{
		id: 'last7days',
		label: 'Last 7 days',
		value: {
			startDate: new Date(
				new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).setHours(0, 0, 0, 0),
			),
			endDate: new Date(new Date().setHours(23, 59, 59, 999)),
		},
	},
	{
		id: 'last30days',
		label: 'Last 30 days',
		value: {
			startDate: new Date(
				new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).setHours(0, 0, 0, 0),
			),
			endDate: new Date(new Date().setHours(23, 59, 59, 999)),
		},
	},
	{
		id: 'thisMonth',
		label: 'This month',
		value: {
			startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
			endDate: new Date(
				new Date().getFullYear(),
				new Date().getMonth() + 1,
				0,
				23,
				59,
				59,
				999,
			),
		},
	},
	{
		id: 'lastMonth',
		label: 'Last month',
		value: {
			startDate: new Date(
				new Date().getFullYear(),
				new Date().getMonth() - 1,
				1,
			),
			endDate: new Date(
				new Date().getFullYear(),
				new Date().getMonth(),
				0,
				23,
				59,
				59,
				999,
			),
		},
	},
	{
		id: 'thisQuarter',
		label: 'This quarter',
		value: (() => {
			const today = new Date();
			const currentQuarter = getQuarter(today);
			const startMonth = currentQuarter * 3;
			const endMonth = startMonth + 3;
			return {
				startDate: new Date(today.getFullYear(), startMonth, 1),
				endDate:
					endMonth < 12
						? new Date(today.getFullYear(), endMonth, 0, 23, 59, 59, 999)
						: new Date(today.getFullYear(), 11, 31, 23, 59, 59, 999),
			};
		})(),
	},
	{
		id: 'lastQuarter',
		label: 'Last quarter',
		value: (() => {
			const today = new Date();
			const currentQuarter = getQuarter(today);
			const lastQuarter = currentQuarter === 0 ? 3 : currentQuarter - 1;
			const year =
				currentQuarter === 0 ? today.getFullYear() - 1 : today.getFullYear();
			const startMonth = lastQuarter * 3;
			const endMonth = startMonth + 3;
			return {
				startDate: new Date(year, startMonth, 1),
				endDate: new Date(year, endMonth, 0, 23, 59, 59, 999),
			};
		})(),
	},
	{
		id: 'thisYear',
		label: 'This year',
		value: {
			startDate: new Date(new Date().getFullYear(), 0, 1),
			endDate: new Date(new Date().getFullYear(), 11, 31, 23, 59, 59, 999),
		},
	},
	{
		id: 'lastYear',
		label: 'Last year',
		value: {
			startDate: new Date(new Date().getFullYear() - 1, 0, 1),
			endDate: new Date(new Date().getFullYear() - 1, 11, 31, 23, 59, 59, 999),
		},
	},
	{
		id: 'custom',
		label: 'Custom',
		value: {
			startDate: null,
			endDate: null,
		},
	},
];

interface DateRangePillSelectorProps {
	selectedValue: string;
	onSelect: (rangeId: string) => void;
	onCustomRangeClick?: () => void;
	currentRange: { label: string; startDate: string; endDate: string };
	customOptions?: Array<{ value: string; label: string }>;
	className?: string;
	pillClassName?: string;
	disabled?: boolean;
	/** Optional label displayed above the pills. Defaults to "Date Range" */
	label?: string;
	/** When true, hides the "Custom" date range option */
	hideCustomOption?: boolean;
}

export function DateRangePillSelector({
	selectedValue,
	onSelect,
	onCustomRangeClick,
	currentRange,
	customOptions,
	className,
	pillClassName,
	disabled = false,
	label = 'Date Range',
	hideCustomOption = false,
}: DateRangePillSelectorProps) {
	const colors = useThemeColors();
	const { t } = useTranslation();

	// Use custom options if provided, otherwise use default
	const baseOptions = customOptions
		? [
				...customOptions.map((opt) => ({
					id: opt.value,
					label: opt.label,
					value: { startDate: null, endDate: null },
				})),
				// Add custom range option at the end unless hidden
				...(!hideCustomOption
					? [
							{
								id: 'custom',
								label: 'Custom',
								value: {
									startDate: null as Date | null,
									endDate: null as Date | null,
								},
							},
						]
					: []),
			]
		: DATE_RANGE_OPTIONS;

	const options = hideCustomOption
		? baseOptions.filter((opt) => opt.id !== 'custom')
		: baseOptions;

	const handlePillClick = (rangeId: string) => {
		if (disabled) {
			return;
		}

		if (rangeId === 'custom') {
			onCustomRangeClick?.();
		} else {
			onSelect(rangeId);
		}
	};

	return (
		<div className={cn('mb-4', className)}>
			{label != null && (
				<Text
					className={cn(
						'block font-medium text-xs mb-2',
						colors.classes.textPrimary,
					)}
				>
					{t(label)}
				</Text>
			)}
			<div className="flex flex-wrap gap-1.5">
				{options.map((option) => (
					<button
						key={option.id}
						onClick={() => {
							handlePillClick(option.id);
						}}
						disabled={disabled}
						className={cn(
							'px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors',
							'border border-gray-200 dark:border-gray-700',
							selectedValue === option.id
								? 'bg-accent-badge text-accent-tint-fg border-accent-tint'
								: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-accent-badge hover:border-accent-tint',
							disabled && 'opacity-50 cursor-not-allowed',
							pillClassName,
						)}
					>
						{t(option.label)}
						{option.id === 'custom' && selectedValue === 'custom' && (
							<Icon
								icon={faCalendar}
								className="ml-1 inline-block h-2.5 w-2.5"
							/>
						)}
					</button>
				))}
			</div>
		</div>
	);
}

// Helper function to get date range by ID
export function getDateRangeById(id: string): DateRangeOption | undefined {
	return DATE_RANGE_OPTIONS.find((option) => option.id === id);
}

// Helper function to format date range for display
export function formatDateRange(
	startDate: Date | null,
	endDate: Date | null,
): string {
	if (!startDate || !endDate) {
		return 'Select dates';
	}

	const formatDate = (date: Date) =>
		date.toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
		});

	if (startDate.toDateString() === endDate.toDateString()) {
		return formatDate(startDate);
	}

	return `${formatDate(startDate)} - ${formatDate(endDate)}`;
}
